import { isDefined, isNotDefined } from '@sgme/fp';
import { GreeksTable } from 'state/quotes/details/details.model';
import { StreamingPriceModel, StreamingUnderlying } from 'state/quotes/streaming/streaming.model';
import { getFormEnrichedUnderlying } from 'state/selectors';
import { AppState } from 'state/state.model';
import { Amount } from 'utils/streaming/quotePriceEvent';

function getStreamingPriceByTabId(
  state: AppState,
  tabId: string,
  sequenceId?: number,
): StreamingPriceModel | undefined {
  const prices = state.quotes.streaming[tabId];
  if (isNotDefined(prices)) {
    return undefined;
  }
  if (isDefined(sequenceId)) {
    return prices[sequenceId];
  }
  const sequenceIds = Object.keys(prices).map(Number);
  const maxSequenceId = sequenceIds.reduce((v1, v2) => (v1 > v2 ? v1 : v2)) as number;
  const price = prices[maxSequenceId];
  return price;
}
export function hasStreamingPriceByTabId(state: AppState, tabId: string): boolean {
  return isDefined(getStreamingPriceByTabId(state, tabId));
}

function getSolvingParamByTabId(state: AppState, tabId: string) {
  const streamingPrice = getStreamingPriceByTabId(state, tabId);
  if (isNotDefined(streamingPrice?.solvingParameters)) {
    return undefined;
  }
  return Object.entries(streamingPrice?.solvingParameters).at(0);
}

export function getStreamingSolvingModeByTabId(state: AppState, tabId: string): string | undefined {
  const solvingParam = getSolvingParamByTabId(state, tabId);
  if (isNotDefined(solvingParam)) {
    return undefined;
  }
  return solvingParam[0];
}
export function getStreamingSolvingAmountInPercentByTabId(state: AppState, tabId: string): Amount | undefined {
  const solvingParam = getSolvingParamByTabId(state, tabId);
  if (isNotDefined(solvingParam)) {
    return undefined;
  }
  return solvingParam[1].find(a => a.unit === '%');
}
export function getStreamingSolvingAmountInCurrencyByTabId(state: AppState, tabId: string): Amount | undefined {
  const solvingParam = getSolvingParamByTabId(state, tabId);
  if (isNotDefined(solvingParam)) {
    return undefined;
  }
  return solvingParam[1].find(a => a.unit !== '%');
}
export function getStreamingSequence(state: AppState, tabId: string): number | undefined {
  return getStreamingPriceByTabId(state, tabId)?.quoteSequenceId;
}
export function getStreamingUnderlying(state: AppState, tabId: string): StreamingUnderlying | undefined {
  const streamedUnderlying = getStreamingPriceByTabId(state, tabId)?.underlying;
  const underlying = getFormEnrichedUnderlying(state, tabId);
  if (isNotDefined(streamedUnderlying) || isNotDefined(underlying)) {
    return undefined;
  }
  return { ...streamedUnderlying, currency: underlying.currency };
}

export function getStreamingDetails(
  state: AppState,
  tabId: string,
): Readonly<Record<string, Readonly<unknown>>> | undefined {
  const streaming = getStreamingPriceByTabId(state, tabId);
  if (!isDefined(streaming)) {
    return undefined;
  }
  const { greeks: _, ...remainingValues } = streaming.remainingValues;
  if (streaming.fxRate?.code) {
    return { [streaming.fxRate?.code]: streaming.fxRate.referencePrice, ...remainingValues };
  }
  return remainingValues;
}
export function getStreamingGreeks(state: AppState, tabId: string): GreeksTable | undefined {
  const streaming = getStreamingPriceByTabId(state, tabId);

  return streaming?.greeks;
}
